// @flow
import truncate from "truncate";
import createPlaceMetaTitle from "./metaDataPlaceTitle";
import stripHtml from "../../../app/assets/pois/utils/stripHtml";
import listItemTypes from "../../../app/assets/shared/listItemTypes";
import resourceTypes from "../open-planet/resourceTypes";
import type { Place, Poi, KindEnum, ResourceEnum } from "../../types";
import { createListUrl, createDetailUrlFromResource } from "../../utils/createUrls";
import returnBreadcrumbs from "./breadcrumbs";

type MetaData = {
  kind?: KindEnum,
  resource?: ResourceEnum,
  items?: Array<Object>,
} & Poi;

/**
 * Returns an object containing data for meta content
 * @param  {Object} data            An object with keys that should match the keys being returned
 * @param  {Object} place Place data object
 * @param  {String} type            The type of page (list, poi, partnerActivity)
 * @return {Object}
 */
export default function createMetaData(data: MetaData, place: Place, type: "list" | "detail") {
  const setListItemType = () => {
    const listItemType = listItemTypes[data.kind] || listItemTypes[data.resource];

    if (listItemType) {
      return listItemType.title;
    }

    return "";
  };

  const createTitle = () => {
    let title = "";

    if (type === "list") {
      title = `${setListItemType()}`;
    }

    if (type === "detail") {
      title = data.name || "";
    }

    if (data.resource === resourceTypes.collections) {
      if (data.title === "Sleeping") {
        return `Best hotels and hostels to stay${createPlaceMetaTitle(place)}`;
      }

      if (data.name) {
        return `Best ${data.name} in ${place.name}`;
      }

      return `Best collections in ${place.name}`;
    }

    return `${title}${createPlaceMetaTitle(place)}`;
  };

  const createDescription = () => {
    let description = "";
    let title = "";
    const alternateDescription = createTitle();

    if (type === "list") {
      const poiCount = data.subtypes ? data.subtypes.reduce((m, s) => m + s.count, 0) : "";
      const prefix = `Discover the best ${poiCount ? `${poiCount} ` : ""}`;
      const suffix = place.name ? ` in ${place.name}` : "";
      const including = data.items ? data.items.slice(0, 3)
        .filter(i => i.name)
        .map(i => i.name).join(", ") : "";

      title = setListItemType();
      description = title ? `${prefix}${title.toLowerCase()}${suffix}${including ? ` including ${including}` : ""}.` : "";
    }

    if (type === "detail") {
      if (data.type === resourceTypes.lodging) {
        description = data.providers ?
          stripHtml(data.providers[0].description && data.providers[0].description.length
            ? data.providers[0].description[0].text : alternateDescription)
          : alternateDescription;
      } else if (data.type === resourceTypes.collections) {
        if (data.title === "Sleeping") {
          description = `Our independent authors have visited ${place.name} and picked these hotels and hostels as the best of the best.`
        } else {
          description = `Our independent authors have visited ${place.name} and picked these ${data.name} as the best of the best.`
        }
      } else {
        description = data.review ? stripHtml(data.review.essential) : alternateDescription;
      }
    }

    if (data.type === resourceTypes.partner_activity) {
      if (data.descriptions && data.descriptions.short_description) {
        description = stripHtml(data.descriptions.short_description);
      } else if (data.descriptions && data.descriptions.long_description) {
        description = stripHtml(data.descriptions.long_description);
      } else {
        description = alternateDescription;
      }
    }

    return description ? truncate(description, 160) : "";
  };

  const createCanonicalLink = () => {
    if (!place.id) return "";
    let url = "";
    const breadcrumbs = returnBreadcrumbs(place.ancestry, place).slice(0, 3).reverse();
    const breadCrumbTitles = breadcrumbs.map(b => b.title);
    if (type === "list") {
      url = createListUrl({
        breadcrumb: breadCrumbTitles,
        kind: data.kind,
        placeId: place.id,
        resource: data.resource,
      });
    } else {
      url = createDetailUrlFromResource(
        data,
        breadCrumbTitles,
        breadcrumbs[breadcrumbs.length - 1].id
      );
    }
    return `https://www.lonelyplanet.com${url}`;
  };

  return {
    title: createTitle(),
    description: createDescription(),
    canonical: createCanonicalLink(),
  };
}



// WEBPACK FOOTER //
// ../server/lib/helpers/metaData.js